import React, { useState } from 'react';
import { Bell, Mail, Clock, Monitor, Save, AlertCircle, Info } from 'lucide-react';

// Badge que indica que el campo es referencial (no tiene efecto funcional aún)
const BadgeReferencial = () => (
  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 border border-amber-200">
    <Info size={10} />
    Referencial
  </span>
);

// Toggle con el mismo estilo que "Aplicar IVA" en Contenedores
const Toggle = ({ value, onChange, activeLabel, inactiveLabel }) => (
  <div className="flex items-center gap-2">
    <button
      type="button"
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
        value ? 'bg-blue-600' : 'bg-gray-300'
      }`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        value ? 'translate-x-6' : 'translate-x-1'
      }`} />
    </button>
    {(activeLabel || inactiveLabel) && (
      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
        value ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
      }`}>
        {value ? (activeLabel || 'ON') : (inactiveLabel || 'OFF')}
      </span>
    )}
  </div>
);

function TabAlertas({ configuracion, onSave, showToast, setHasChanges }) {
  const [formData, setFormData] = useState({
    push_activo: configuracion.push_activo ?? true,
    push_sonido: configuracion.push_sonido ?? true,
    push_notificaciones_intervalo: configuracion.push_notificaciones_intervalo || 60,
    notif_alerta_alta: configuracion.notif_alerta_alta ?? true,
    notif_critica: configuracion.notif_critica ?? true,
    notif_nuevo: configuracion.notif_nuevo ?? false,
    notif_recalculo: configuracion.notif_recalculo ?? false,
    email_admin: configuracion.email_admin || '',
    email_notificaciones_activo: configuracion.email_notificaciones_activo ?? false,
    inactividad_timeout: configuracion.inactividad_timeout || 30,
  });

  const [saving, setSaving] = useState(false);

  const handleToggle = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
    setHasChanges(true);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleNumberChange = (field, value) => {
    const num = parseInt(value, 10);
    if (!isNaN(num)) {
      setFormData(prev => ({ ...prev, [field]: num }));
      setHasChanges(true);
    }
  };

  const handleSave = async () => {
    if (saving) return;

    if (formData.push_notificaciones_intervalo < 10 || formData.push_notificaciones_intervalo > 600) {
      showToast('El intervalo de notificaciones debe estar entre 10 y 600 segundos', 'error');
      return;
    }

    if (formData.inactividad_timeout < 3 || formData.inactividad_timeout > 480) {
      showToast('El timeout de inactividad debe estar entre 3 y 480 minutos', 'error');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Bell size={24} className="text-ink-700" />
        <div>
          <h2 className="text-xl font-semibold text-ink-900">Sistema de Alertas</h2>
          <p className="text-sm text-ink-500">Notificaciones push, email y sesión</p>
        </div>
      </div>

      {/* Aviso general */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <p className="font-medium">Nota sobre Email</p>
          <p className="mt-0.5">El envío de correo electrónico está marcado como <span className="font-semibold">Referencial</span> — guarda los valores pero aún no hay servidor de email configurado. El resto de ajustes en esta sección son funcionales.</p>
        </div>
      </div>

      {/* Notificaciones Push */}
      <div className="border border-ink-200 rounded-lg p-6 bg-white">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Monitor size={20} className="text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-ink-900">Notificaciones Push</h3>
            <p className="text-sm text-ink-500">Alertas emergentes del sistema operativo</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* push_activo */}
          <div className="flex items-center justify-between py-3 border-b border-ink-100">
            <div>
              <p className="text-sm font-medium text-ink-800">Activar notificaciones push</p>
            </div>
            <Toggle
              value={formData.push_activo}
              onChange={() => handleToggle('push_activo')}
              activeLabel="ACTIVO"
              inactiveLabel="INACTIVO"
            />
          </div>

          {/* push_sonido */}
          <div className="flex items-center justify-between py-3 border-b border-ink-100">
            <div>
              <p className="text-sm font-medium text-ink-800">Reproducir sonido</p>
            </div>
            <Toggle
              value={formData.push_sonido}
              onChange={() => handleToggle('push_sonido')}
              activeLabel="CON SONIDO"
              inactiveLabel="SIN SONIDO"
            />
          </div>

          {/* push_notificaciones_intervalo */}
          <div className="pt-2">
            <label className="block text-sm font-medium text-ink-700 mb-2">
              Intervalo de verificación (segundos)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={formData.push_notificaciones_intervalo}
                onChange={(e) => handleNumberChange('push_notificaciones_intervalo', e.target.value)}
                min="10"
                max="600"
                step="5"
                className="w-36 px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
              />
              <span className="text-sm text-ink-500">
                = cada {formData.push_notificaciones_intervalo >= 60
                  ? `${Math.floor(formData.push_notificaciones_intervalo / 60)}min${formData.push_notificaciones_intervalo % 60 > 0 ? ` ${formData.push_notificaciones_intervalo % 60}s` : ''}`
                  : `${formData.push_notificaciones_intervalo}s`}
              </span>
            </div>
            <p className="text-xs text-ink-400 mt-1">Mínimo 10s — Máximo 600s. Aplica al reiniciar sesión.</p>
          </div>
        </div>
      </div>

      {/* Tipos de Notificaciones */}
      <div className="border border-ink-200 rounded-lg p-6 bg-white">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
            <Bell size={20} className="text-orange-600" />
          </div>
          <div>
            <h3 className="font-semibold text-ink-900">Tipos de Notificaciones</h3>
            <p className="text-sm text-ink-500">Qué eventos disparan el popup del navegador</p>
          </div>
        </div>

        <div className="space-y-4">
          {[
            { field: 'notif_alerta_alta', label: 'Alerta alta', desc: 'Contenedor entra en nivel de alerta alta' },
            { field: 'notif_critica', label: 'Alerta crítica', desc: 'Contenedor entra en estado crítico' },
            { field: 'notif_nuevo', label: 'Nuevo contenedor', desc: 'Se registra un nuevo contenedor' },
            { field: 'notif_recalculo', label: 'Recálculo ejecutado', desc: 'El sistema ejecuta un recálculo de demoras' },
          ].map(({ field, label, desc }) => (
            <div key={field} className="flex items-center justify-between py-3 border-b border-ink-100 last:border-0">
              <div>
                <p className="text-sm font-medium text-ink-800">{label}</p>
                <p className="text-xs text-ink-400 mt-0.5">{desc}</p>
              </div>
              <Toggle
                value={formData[field]}
                onChange={() => handleToggle(field)}
                activeLabel="SÍ"
                inactiveLabel="NO"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Email */}
      <div className="border border-ink-200 rounded-lg p-6 bg-white">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Mail size={20} className="text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-ink-900">Notificaciones por Email</h3>
              <p className="text-sm text-ink-500">Alertas enviadas al correo del administrador</p>
            </div>
          </div>
          <BadgeReferencial />
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-ink-100">
            <p className="text-sm font-medium text-ink-800">Activar notificaciones por email</p>
            <Toggle
              value={formData.email_notificaciones_activo}
              onChange={() => handleToggle('email_notificaciones_activo')}
              activeLabel="ACTIVO"
              inactiveLabel="INACTIVO"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-700 mb-2">
              Email del administrador
            </label>
            <input
              type="email"
              value={formData.email_admin}
              onChange={(e) => handleChange('email_admin', e.target.value)}
              placeholder="admin@empresa.com"
              className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
            />
            <p className="text-xs text-ink-400 mt-1">Destinatario de las notificaciones de alerta</p>
          </div>
        </div>
      </div>

      {/* Timeout de Sesión */}
      <div className="border border-ink-200 rounded-lg p-6 bg-white">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <Clock size={20} className="text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-ink-900">Timeout de Sesión</h3>
            <p className="text-sm text-ink-500">Cierre automático por inactividad</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-ink-700 mb-2">
            Tiempo de inactividad (minutos)
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={formData.inactividad_timeout}
              onChange={(e) => handleNumberChange('inactividad_timeout', e.target.value)}
              min="3"
              max="480"
              step="1"
              className="w-36 px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent"
            />
            <span className="text-sm text-ink-500">
              {formData.inactividad_timeout >= 60
                ? `${Math.floor(formData.inactividad_timeout / 60)}h ${formData.inactividad_timeout % 60 > 0 ? `${formData.inactividad_timeout % 60}min` : ''}`
                : `${formData.inactividad_timeout} minutos`}
            </span>
          </div>
          <p className="text-xs text-ink-400 mt-1">Mínimo 3 min — Máximo 480 min. Aplica al reiniciar sesión.</p>
        </div>
      </div>

      {/* Botón Guardar */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-ink-900 text-white rounded-lg hover:bg-ink-800 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Guardando...</span>
            </>
          ) : (
            <>
              <Save size={18} />
              <span>Guardar Configuración</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default TabAlertas;
